import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageTitle from '../../components/common/PageTitle';
import ServicesCard from '../../components/cards/ServicesCard';
import LeftImage from '../../components/common/LeftImage';
import SectionHeader from '../../components/common/SectionHeader';
import { useDispatch, useSelector } from 'react-redux';
import { getOtherServices, getSingleData } from '../../services/actions/MainAction';
import { 
  Zap, 
  Clock, 
  Shield, 
  CheckCircle, 
  ArrowRight, 
  Sparkles,
  Calendar,
  Users,
  Award,
  Wrench,
  ChevronRight,
  Phone,
  Mail,
  MessageCircle
} from 'lucide-react';

function SingleService() {
  const contentData = useSelector(state => state.Data.contentData);
  const otherServices = useSelector(state => state.Data.otherServices);
  const lang_id = useSelector(state => state.Data.lang_id);
  const contactData = useSelector(state => state.Data.contactData);
  const [singleData, setSingleData] = useState(null);
  const params = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(getSingleData(`services/${params?.id}?lang_id=${lang_id}`))
      .then(resp => {
        setSingleData(resp);
        if(resp) {
          dispatch(getOtherServices(lang_id, resp?.id));
        }
      });
  }, [params?.id, lang_id, dispatch]);

  // Service features
  const serviceFeatures = [
    { icon: <Clock className="w-5 h-5" />, text: "Sürətli Xidmət", color: "text-blue-600" },
    { icon: <Shield className="w-5 h-5" />, text: "Zəmanətli İş", color: "text-green-600" },
    { icon: <Award className="w-5 h-5" />, text: "Professional Komanda", color: "text-amber-600" },
    { icon: <Wrench className="w-5 h-5" />, text: "Müasir Avadanlıq", color: "text-purple-600" }
  ];

  // Process steps
  const processSteps = [
    { number: "01", title: "Müraciət", description: "Bizimlə əlaqə saxlayın" },
    { number: "02", title: "Təhlil", description: "Problem təhlili" },
    { number: "03", title: "Həll", description: "Professional həll" },
    { number: "04", title: "Nəzarət", description: "Keyfiyyət nəzarəti" }
  ];

  if (!singleData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Yüklənir...</p>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 py-16">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 border-2 border-blue-400 rounded-full animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-48 h-48 border-2 border-amber-400 rounded-full animate-pulse"></div>
        </div>

        <div className="relative container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 rounded-full mb-6 border border-blue-400/30 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-blue-300" />
              <span className="text-sm font-medium text-blue-100">Xidmət</span>
              <Sparkles className="w-4 h-4 text-amber-300" />
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              <span className="bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent">
                {singleData?.translate?.title}
              </span>
            </h1>
            
            <p className="text-lg text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Professional elektrik xidmətləri və peşəkar həllər
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className=" mx-auto">
            <div className="grid ">
              {/* Left Column - Image & Quick Info */}
              <div className="lg:col-span-2 space-y-8">
                {/* Service Image */}
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <div className="relative h-64 md:h-80 lg:h-96">
                    <img 
                      src={singleData?.image_full_url} 
                      alt={singleData?.translate?.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                    
                    {/* Badge */}
                    <div className="absolute top-6 right-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                        <Zap className="w-7 h-7 text-white" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Image Caption */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                    <div className="text-white">
                      <div className="text-sm font-medium text-blue-300 mb-1">Professional Xidmət</div>
                      <h3 className="text-xl font-bold">{singleData?.translate?.title}</h3>
                    </div>
                  </div>
                </div>

                {/* Service Features */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {serviceFeatures.map((feature, index) => (
                    <div key={index} className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className={`${feature.color} mb-2`}>
                        {feature.icon}
                      </div>
                      <div className="text-sm font-medium text-gray-900">{feature.text}</div>
                    </div>
                  ))}
                </div>

                {/* Service Description */}
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Xidmət Haqqında</h2>
                      <p className="text-gray-600">Ətraflı məlumat və təfərrüatlar</p>
                    </div>
                  </div>
                  
                  <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                    <div dangerouslySetInnerHTML={{__html: singleData?.translate?.description}} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full mb-4 border border-blue-100">
                <Sparkles className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium text-blue-700">Digər Xidmətlər</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {contentData?.other_service || "Digər Xidmətlərimiz"}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Professional elektrik xidmətlərimizlə tanış olun
              </p>
              
              <div className="h-1 w-24 bg-gradient-to-r from-blue-500 to-amber-500 mx-auto mt-6 rounded-full"></div>
            </div>

            {/* Other Services Grid */}
            {otherServices?.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {otherServices.map((data, i) => (
                  <ServicesCard key={i} data={data} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                Hal-hazırda digər xidmətlər mövcud deyil
              </div>
            )}

            {/* View All Button */}
            <div className="text-center mt-12">
              <Link
                to="/services"
                className="inline-flex items-center gap-3 px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <span>Bütün Xidmətlər</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default SingleService;